import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Headphones, Star, Sparkles } from 'lucide-react';
import type { PodcastWithRelations } from '../../../hooks/usePodcasts';
import { PlayButton } from './PlayButton';

interface FeaturedEpisodeProps {
  podcast: PodcastWithRelations;
  currentPodcast: PodcastWithRelations | null;
  isPlaying: boolean;
  loadingAudio: boolean;
  onPlayPause: (podcast: PodcastWithRelations) => void;
}

export const FeaturedEpisode: React.FC<FeaturedEpisodeProps> = ({
  podcast,
  currentPodcast,
  isPlaying,
  loadingAudio,
  onPlayPause
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative h-[860px] rounded-2xl overflow-hidden group"
    >
      {/* Animated particles */}
      <div className="absolute inset-0 pointer-events-none z-10">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-primary/40 rounded-full"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0, 0.8, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: 2 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 5,
            }}
          />
        ))}
      </div>
      
      {/* Background Image with Gradient */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
        style={{ backgroundImage: `url(${podcast.image_url})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />

      {/* Content */}
      <div className="absolute inset-0 p-8 flex flex-col justify-end">
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="flex items-center gap-2 px-3 py-1 bg-primary text-primary-foreground rounded-full text-sm">
              <Star className="w-4 h-4" />
              <motion.span
                animate={{ 
                  scale: [1, 1.1, 1],
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                Em Destaque
              </motion.span>
            </span>
            <span className="px-3 py-1 bg-secondary/20 backdrop-blur-sm rounded-full text-sm">
              {podcast.category?.name}
            </span>
          </div>

          <h2 className="text-4xl font-bold mb-4 group-hover:text-primary transition-colors max-w-2xl">
            {podcast.title}
          </h2>
          
          {/* Animated underline */}
          <motion.div
            className="w-24 h-1 bg-gradient-to-r from-primary to-purple-500 rounded-full mb-4"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 96, opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.8 }}
          />

          <p className="text-gray-300 mb-8 line-clamp-3 max-w-2xl text-lg">
            {podcast.description}
          </p>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8">
            <PlayButton
              podcast={podcast}
              currentPodcast={currentPodcast}
              isPlaying={isPlaying}
              loadingAudio={loadingAudio}
              onPlayPause={onPlayPause}
              size="lg"
              variant="full" 
            />

            <div className="flex flex-wrap items-center gap-4 sm:gap-8 text-base text-gray-300 mt-4 sm:mt-0">
              <span className="flex items-center gap-2 whitespace-nowrap">
                <Calendar className="w-5 h-5" />
                {new Date(podcast.published_at || '').toLocaleDateString('pt-PT')}
              </span>
              <span className="flex items-center gap-2 whitespace-nowrap">
                <Clock className="w-5 h-5" />
                {podcast.duration}
              </span>
              <span className="flex items-center gap-2 whitespace-nowrap">
                <Headphones className="w-5 h-5" />
                {podcast.plays} plays
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};